import { Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import { TemplateConfig } from "@types";
import { getSubscription, isTaskVisible } from "@utils";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { DemoCompleteModal } from "src/components/DemoCompleteModal/DemoCompleteModal";
import { EditDeviceModalContent } from "src/components/DeviceCreation/modal-list-items/EditDeviceModalContent";
import { Cards } from "src/components/Roadmap/Cards/Cards";
import { areAllCardTasksCompleted } from "src/components/Roadmap/utils";
import { getOrgFromUser } from "src/utils/user";
import { RoadmapSkeleton, TaskModal } from "../components";
import PageTitle from "../components/PageTitle";
import {
  useGetDevice,
  useGetDevices,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetRoadmapCards,
  useGetRoadmapStages,
  useGetTasks,
  useGetUser,
} from "../hooks";
import { useStore } from "../stores";
import { Device, Document, TEMPLATE_ID, User } from "../stores/models";

export const Roadmap = () => {
  const { deviceStore } = useStore();
  const { deviceId = "", orgId = "" } = useParams();
  if (!deviceId) throw new Error("No device ID provided");
  const { data: devices } = useGetDevices({
    orgId,
  });
  const [selectedCard, setSelectedCard] = useState<
    { cardKey: string; stageKey: string } | undefined
  >();
  const [editDevice, setEditDevice] = useState(false);
  let [_, setSearchParams] = useSearchParams();

  const { data: user } = useGetUser();
  const { data: device } = useGetDevice({ orgId, deviceId });
  const org = getOrgFromUser(user, orgId);
  const subscription = org ? getSubscription(org) : null;
  const { isLoading: tasksLoading, data: tasks } = useGetTasks(orgId, deviceId);
  const { data: cards } = useGetRoadmapCards({ orgId, deviceId });
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId, templatesConfig);
  const { data: stages } = useGetRoadmapStages({ orgId, deviceId });

  const checkCardCompletion = useMemo(
    () =>
      (
        cardTasks: string[],
        documents: Document[],
        user: User,
        device: Device,
        templatesConfig: TemplateConfig
      ) => {
        const cardDocuments = documents.filter((document) =>
          cardTasks.includes(document.templateKey as TEMPLATE_ID)
        );

        return areAllCardTasksCompleted(
          cardTasks,
          cardDocuments,
          user,
          documents,
          orgId,
          device,
          templatesConfig,
          tasks
        );
      },
    [documents, user, orgId, device]
  );

  if (
    !device ||
    tasksLoading ||
    !tasks ||
    !documents ||
    !user ||
    !subscription ||
    !devices ||
    !templatesConfig ||
    !stages
  ) {
    return <RoadmapSkeleton />;
  }

  const roadmap = deviceStore.mapRoadmap(tasks, device, cards, stages);

  const handleClickOpenCardDialog = (cardKey: string, stageKey: string) => {
    setSelectedCard({ cardKey, stageKey });
  };

  const handleCloseCardDialog = () => {
    setSearchParams((params) => {
      params.delete("task");
      return params;
    });
    setSelectedCard(undefined);
  };

  const card = roadmap
    .find((step) => step.id === selectedCard?.stageKey)
    ?.cards.find((card) => card.cardKey === selectedCard?.cardKey);

  const first =
    "polygon(0 0,calc(100% - 6px) 0,100% 50%,calc(100% - 6px) 100%,0 100%)";
  const all =
    "polygon(calc(100% - 6px) 0,100% 50%,calc(100% - 6px) 100%,2px 100%,8px 50%,2px 0)";

  return (
    <div className="flex flex-col gap-y-3">
      <PageTitle
        title={device.name}
        enableEdit
        editOnClick={() => setEditDevice(true)}
        isDemoDevice={!!device.isDemoDevice}
      />
      {!!device.isDemoDevice && <DemoCompleteModal orgId={orgId} />}
      <Typography variant="h2">Roadmap</Typography>

      <div className="flex flex-col">
        <div className="flex">
          {roadmap.map((step, index) => {
            const stagesCardsStatus = step.cards.map((card) => {
              const cardTasks = tasks
                .filter(
                  (task) =>
                    task.parentCardKey === card.cardKey &&
                    isTaskVisible(task, device, documents)
                )
                .map((task) => task.taskKey);

              return checkCardCompletion(
                cardTasks,
                documents,
                user,
                device,
                templatesConfig
              );
            });

            const areAllStagesDocumentsCompleted = stagesCardsStatus.every(
              (status) => status === true
            );

            const percentageEach = 100 / stagesCardsStatus.length;
            const percentageCompleted = parseFloat(
              stagesCardsStatus
                .reduce(
                  (acc, status) => (status ? acc : acc - percentageEach),
                  100
                )
                .toFixed(2)
            );

            const color1 = "rgb(21,128, 61)";
            const color2 = "rgb(203, 213, 225)";

            return (
              <div key={step.title} className="flex flex-1 flex-col">
                <div
                  className={`flex flex-1 items-center justify-center px-4 py-1 `}
                  style={{
                    background: `linear-gradient(to right,  ${color1}, ${color1} ${percentageCompleted}%, ${color2} ${percentageCompleted}%, ${color2} ${100 - percentageCompleted}%)`,
                    color: areAllStagesDocumentsCompleted ? "white" : "black",
                    clipPath: index === 0 ? first : all,
                    display: "flex",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  {step.title}
                </div>
              </div>
            );
          })}
        </div>
        <Cards
          roadmap={roadmap}
          onClickOpenCardDialog={handleClickOpenCardDialog}
          documents={documents}
          user={user}
          device={device}
          templatesConfig={templatesConfig}
          tasks={tasks}
        />
      </div>
      <Dialog
        open={editDevice}
        onClose={() => setEditDevice(false)}
        maxWidth={"sm"}
      >
        <DialogTitle>Edit Device</DialogTitle>
        <DialogContent>
          <EditDeviceModalContent
            subscription={subscription}
            org={user.organizationMemberships[0].organization}
            device={device}
            setEditDevice={setEditDevice}
            devices={devices}
          />
        </DialogContent>
      </Dialog>
      {!!selectedCard && !!card && (
        <TaskModal
          cardConfig={{ card, stageKey: selectedCard.stageKey }}
          open={!!selectedCard}
          deviceId={deviceId}
          handleCloseCardDialog={handleCloseCardDialog}
          orgId={orgId}
        />
      )}
    </div>
  );
};
